import React, { useEffect } from 'react';
import ThankYouConfirmation, {
    redirectFromThankYou,
} from '../../components/forms/questions/ThankYouConfirmation.jsx';
import FormSection from './FormSection.jsx';
import { ConfirmationContext } from '../../lib/ConfirmationContext.js';
import { useNavigate } from 'react-router-dom';
import { conversionTrackSubmitted } from '../lib/ConversionEventTracking.js';
import { handleFormSubmissionResponse } from '~/public/lib/handleFormSubmissionResponse.js';
import {
    canScheduleEventType,
    canScheduleBookableResource,
} from '../../lib/FormHelpers.js';

const FormThankYou = ({ form, sections, formSubmissionResults }) => {
    const isBooking = formSubmissionResults?.events?.length > 0;

    const navigate = useNavigate();

    const thankYouSections = sections.filter(
        s => !!s.section.settings?.thankyou,
    );
    const thankYouSectionData =
        thankYouSections.length > 0 ? thankYouSections[0] : null;

    useEffect(() => {
        const confirmationUrl =
            formSubmissionResults?.submission?.confirmation_url;

        if (!form || !confirmationUrl) {
            return;
        }

        const urlConfirmationUrl = new URL(confirmationUrl);

        if (document.location.pathname !== urlConfirmationUrl.pathname) {
            // We just submitted
            if (!handleFormSubmissionResponse(formSubmissionResults)) {
                navigate(urlConfirmationUrl.pathname);
            }
        }
    }, []);

    let schedulingSettings = {};
    if (isBooking) {
        // Find the settings for the scheduling section that matches the first booked event
        const firstEvent = formSubmissionResults?.events?.[0];
        if (firstEvent) {
            const eventTypeId = firstEvent.event_type_id;
            const bookableResourceId = firstEvent.pivot?.bookable_resource_id;

            let firstSchedulingQuestion = null;

            for (const s of sections) {
                for (const q of s.questions) {
                    if (q.type === 'scheduling' || q.type === 'classscheduling') {
                        const settings = q.settings || {};

                        // Keep track of the first scheduling question as fallback
                        if (!firstSchedulingQuestion) {
                            firstSchedulingQuestion = settings;
                        }

                        // Check if this question can schedule the event type and bookable resource
                        const eventTypeMatches = canScheduleEventType(
                            eventTypeId,
                            settings,
                        );
                        const bookableResourceMatches = canScheduleBookableResource(
                            bookableResourceId,
                            settings,
                        );

                        // If both match, this is the right scheduling question
                        if (eventTypeMatches && bookableResourceMatches) {
                            schedulingSettings = settings;
                            break;
                        }
                    }
                }
                if (schedulingSettings && Object.keys(schedulingSettings).length > 0) {
                    break;
                }
            }

            // Fallback to the first scheduling question if no match was found
            if (Object.keys(schedulingSettings).length === 0 && firstSchedulingQuestion) {
                schedulingSettings = firstSchedulingQuestion;
            }
        }
    }

    return (
        <div className="form-section-container p-6">
            <div className="form-section form-section-current mx-auto">
                <ConfirmationContext.Provider
                    value={{
                        isBooking: isBooking,
                        events: formSubmissionResults?.events,
                        usedGrants: formSubmissionResults?.usedGrants,
                        giftCertificates:
                            formSubmissionResults?.giftCertificates,
                        submission: formSubmissionResults?.submission,
                        schedulingSettings: schedulingSettings,
                        // For a custom question on the thank you section -
                        //  see ~/lib/CustomQuestions/useConfirmationActions
                        isConfirmationPage: true,
                        customQuestionConfirmation:
                            formSubmissionResults?.customQuestionConfirmation,
                    }}
                >
                    {!!thankYouSectionData && (
                        <FormSection
                            section={thankYouSectionData.section}
                            questions={thankYouSectionData.questions}
                            formData={{}}
                            setFormData={() => null}
                        />
                    )}
                    {!thankYouSectionData && (
                        <ThankYouConfirmation isBooking={isBooking} />
                    )}
                </ConfirmationContext.Provider>
            </div>
        </div>
    );
};

export default FormThankYou;
